import React, { useState, useEffect } from 'react';
import { Card, Spin, Alert, Tabs, Button, Select, DatePicker, Badge, Tooltip, Tag, Space, message } from 'antd';
import {
  CalendarOutlined,
  ClockCircleOutlined,
  UserOutlined,
  TeamOutlined,
  ReloadOutlined,
  PlusOutlined
} from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import { appointmentApi, Appointment } from '../../services/appointmentApi';
import { scheduleApi } from '../../services/scheduleApi';
import './TechScheduleView.css';

dayjs.extend(isoWeek);

const { TabPane } = Tabs;
const { RangePicker } = DatePicker;
const { Option } = Select;

interface Technician {
  id: number;
  first_name: string;
  last_name: string;
  role: string;
  schedule?: any;
}

interface TimeSlot {
  time: string;
  hour: number;
  minute: number;
}

const TechScheduleView: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [technicians, setTechnicians] = useState<Technician[]>([]);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs());
  const [viewMode, setViewMode] = useState<'day' | 'week'>('day');
  const [selectedTech, setSelectedTech] = useState<number | null>(null);

  useEffect(() => {
    loadData();
  }, [selectedDate, viewMode]);

  const loadData = async () => {
    try {
      setLoading(true);

      // Load technicians with schedules
      const techsResponse = await scheduleApi.getAllTechniciansWithSchedules();
      setTechnicians(techsResponse);

      // Load appointments for selected date range
      const startDate = viewMode === 'day'
        ? selectedDate.startOf('day').toISOString()
        : selectedDate.startOf('week').toISOString();

      const endDate = viewMode === 'day'
        ? selectedDate.endOf('day').toISOString()
        : selectedDate.endOf('week').toISOString();

      const appointmentsResponse = await appointmentApi.getAll({
        start_date: startDate,
        end_date: endDate
      });

      setAppointments(appointmentsResponse);
    } catch (error) {
      console.error('Error loading schedule data:', error);
      message.error('Failed to load schedule data');
    } finally {
      setLoading(false);
    }
  };

  const generateTimeSlots = (): TimeSlot[] => {
    const slots: TimeSlot[] = [];
    for (let hour = 7; hour <= 19; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        slots.push({
          time: `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`,
          hour,
          minute
        });
      }
    }
    return slots;
  };

  const timeSlots = generateTimeSlots();

  const getAppointmentsForTechAndTime = (techId: number, date: Dayjs, hour: number, minute: number): Appointment[] => {
    return appointments.filter(apt => {
      const aptStart = dayjs(apt.start_time);
      const aptEnd = dayjs(apt.end_time);
      const slotTime = date.hour(hour).minute(minute);

      // Check if this tech is assigned (either as primary or additional)
      const isAssigned = apt.technicians?.some(t => t.technician_id === techId);

      // Check if appointment overlaps with this time slot
      const overlaps = slotTime.isBetween(aptStart, aptEnd, null, '[)');

      return isAssigned && overlaps;
    });
  };

  const isTechAvailable = (tech: Technician, date: Dayjs): boolean => {
    if (!tech.schedule) return false;
    const dayOfWeek = date.day();
    const daySchedule = tech.schedule.find((s: any) => s.day_of_week === dayOfWeek);
    return daySchedule?.is_active || false;
  };

  const getTechWorkingHours = (tech: Technician, date: Dayjs): { start: string; end: string } | null => {
    if (!tech.schedule) return null;
    const dayOfWeek = date.day();
    const daySchedule = tech.schedule.find((s: any) => s.day_of_week === dayOfWeek);
    if (!daySchedule || !daySchedule.is_active) return null;
    return {
      start: daySchedule.start_time,
      end: daySchedule.end_time
    };
  };

  const isWithinWorkingHours = (tech: Technician, date: Dayjs, hour: number, minute: number): boolean => {
    const workingHours = getTechWorkingHours(tech, date);
    if (!workingHours) return false;

    const [startHour, startMinute] = workingHours.start.split(':').map(Number);
    const [endHour, endMinute] = workingHours.end.split(':').map(Number);

    const slotMinutes = hour * 60 + minute;
    const startMinutes = startHour * 60 + startMinute;
    const endMinutes = endHour * 60 + endMinute;

    return slotMinutes >= startMinutes && slotMinutes < endMinutes;
  };

  const renderAppointmentCard = (apt: Appointment) => {
    const statusColors: Record<string, string> = {
      scheduled: 'blue',
      confirmed: 'green',
      in_progress: 'orange',
      completed: 'default',
      cancelled: 'red'
    };

    const isPrimary = apt.technicians?.find(t => t.is_primary);
    const additionalTechs = apt.technicians?.filter(t => !t.is_primary).length || 0;

    return (
      <Tooltip
        key={apt.id || apt.start_time}
        title={
          <div>
            <div><strong>{apt.title}</strong></div>
            <div>Customer: {apt.customer?.first_name} {apt.customer?.last_name}</div>
            <div>Type: {apt.appointment_type}</div>
            <div>Time: {dayjs(apt.start_time).format('h:mm A')} - {dayjs(apt.end_time).format('h:mm A')}</div>
            {additionalTechs > 0 && <div>+{additionalTechs} additional tech(s)</div>}
          </div>
        }
      >
        <div className="appointment-card" data-status={apt.status}>
          <Tag color={statusColors[apt.status]} style={{ marginBottom: 4 }}>
            {apt.status}
          </Tag>
          <div className="appointment-title">{apt.title}</div>
          <div className="appointment-customer">
            {apt.customer?.first_name} {apt.customer?.last_name}
          </div>
          <div className="appointment-time">
            <ClockCircleOutlined /> {dayjs(apt.start_time).format('h:mm A')}
          </div>
          {additionalTechs > 0 && (
            <Tag icon={<TeamOutlined />} color="purple">
              +{additionalTechs} tech(s)
            </Tag>
          )}
        </div>
      </Tooltip>
    );
  };

  const renderDayView = () => {
    const activeTechs = selectedTech
      ? technicians.filter(t => t.id === selectedTech)
      : technicians;

    return (
      <div className="tech-schedule-day-view">
        <div className="schedule-header">
          <div className="time-column-header">Time</div>
          {activeTechs.map(tech => (
            <div key={tech.id} className="tech-column-header">
              <div className="tech-name">
                <UserOutlined /> {tech.first_name} {tech.last_name}
              </div>
              <div className="tech-role">{tech.role}</div>
              {isTechAvailable(tech, selectedDate) ? (
                <Badge status="success" text="Available" />
              ) : (
                <Badge status="default" text="Off" />
              )}
            </div>
          ))}
        </div>

        <div className="schedule-grid">
          {timeSlots.map((slot, index) => (
            <div key={index} className="schedule-row">
              <div className="time-cell">
                {slot.minute === 0 && (
                  <span className="time-label">{dayjs().hour(slot.hour).minute(0).format('h:mm A')}</span>
                )}
              </div>

              {activeTechs.map(tech => {
                const isAvailable = isWithinWorkingHours(tech, selectedDate, slot.hour, slot.minute);
                const aptInSlot = getAppointmentsForTechAndTime(tech.id, selectedDate, slot.hour, slot.minute);
                const isSlotStart = aptInSlot.length > 0 && aptInSlot.some(apt =>
                  dayjs(apt.start_time).hour() === slot.hour &&
                  dayjs(apt.start_time).minute() === slot.minute
                );

                return (
                  <div
                    key={`${tech.id}-${slot.time}`}
                    className={`schedule-cell ${isAvailable ? 'available' : 'unavailable'} ${aptInSlot.length > 0 ? 'has-appointment' : ''}`}
                    data-tech-id={tech.id}
                    data-time={slot.time}
                  >
                    {isSlotStart && aptInSlot.map(apt => renderAppointmentCard(apt))}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderWeekView = () => {
    const weekStart = selectedDate.startOf('week');
    const days = Array.from({ length: 7 }, (_, i) => weekStart.add(i, 'day'));

    return (
      <div className="tech-schedule-week-view">
        <div className="week-grid">
          <div className="week-header">
            <div className="corner-cell"></div>
            {days.map(day => (
              <div key={day.format('YYYY-MM-DD')} className="day-header">
                <div className="day-name">{day.format('ddd')}</div>
                <div className="day-date">{day.format('MMM D')}</div>
              </div>
            ))}
          </div>

          {technicians.map(tech => (
            <div key={tech.id} className="tech-week-row">
              <div className="tech-label">
                <div className="tech-name">{tech.first_name} {tech.last_name}</div>
                <div className="tech-role">{tech.role}</div>
              </div>

              {days.map(day => {
                const dayAppointments = appointments.filter(apt => {
                  const aptDate = dayjs(apt.start_time);
                  return aptDate.isSame(day, 'day') &&
                    apt.technicians?.some(t => t.technician_id === tech.id);
                });

                const isAvailable = isTechAvailable(tech, day);
                const workingHours = getTechWorkingHours(tech, day);

                return (
                  <div key={day.format('YYYY-MM-DD')} className={`day-cell ${isAvailable ? 'available' : 'unavailable'}`}>
                    {isAvailable && workingHours && (
                      <div className="working-hours">
                        {workingHours.start} - {workingHours.end}
                      </div>
                    )}
                    <div className="day-appointments">
                      <Badge count={dayAppointments.length} style={{ backgroundColor: '#1890ff' }}>
                        <div className="appointments-count">
                          {dayAppointments.length > 0 && (
                            <span>{dayAppointments.length} apt(s)</span>
                          )}
                        </div>
                      </Badge>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="tech-schedule-view">
      <Card>
        <div className="schedule-controls">
          <Space size="middle">
            <Select
              style={{ width: 150 }}
              value={viewMode}
              onChange={setViewMode}
            >
              <Option value="day">Day View</Option>
              <Option value="week">Week View</Option>
            </Select>

            <DatePicker
              value={selectedDate}
              onChange={(date) => date && setSelectedDate(date)}
              format="MMMM D, YYYY"
            />

            <Select
              style={{ width: 200 }}
              placeholder="All Technicians"
              allowClear
              value={selectedTech}
              onChange={setSelectedTech}
            >
              {technicians.map(tech => (
                <Option key={tech.id} value={tech.id}>
                  {tech.first_name} {tech.last_name}
                </Option>
              ))}
            </Select>

            <Button
              icon={<ReloadOutlined />}
              onClick={loadData}
            >
              Refresh
            </Button>

            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                // TODO: Open appointment modal
                message.info('Create appointment modal - to be implemented');
              }}
            >
              New Appointment
            </Button>
          </Space>
        </div>

        <div className="schedule-content">
          {viewMode === 'day' ? renderDayView() : renderWeekView()}
        </div>
      </Card>
    </div>
  );
};

export default TechScheduleView;
